import React, { useCallback, useMemo, useState } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';
import { useIntl } from 'react-intl';
import Paper from '@mui/material/Paper';
import makeStyles from '@mui/styles/makeStyles';
import { useSupervisorInsightContext } from 'modules/Insights/Notes/SupervisorInsightContextProvider';
import { NoteInsightsViewOptions } from 'modules/Insights/Notes/NoteInsightsContextProvider';
import { useMessages } from 'modules/intl';
import { useGetSupervisorAnalyticsData } from 'modules/Insights/Notes/hooks/useGetSupervisorAnalyticsData';
import { OrgAnalyticsDimensionType, OrgAnalyticsContext } from 'graphql/types';
import { useGetPreviousSupervisorData } from 'modules/Insights/Notes/hooks/useGetPreviousSupervisorData';
import { useGetSearchLabels } from 'modules/Insights/hooks/useGetSearchLabels';
import {
  getFormattedPercentage,
  hasValidStartDate
} from 'modules/Insights/utils';
import {
  DropdownLabel,
  transformAnalyticsDataSupervisor,
  trendsData
} from './util';
import Legend from './Legend';

import SourceByTimeChart from './SourceByTimeChart';
const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    padding: `${theme.spacing(2)} ${theme.spacing(2.5)}`
  }
}));
export type SupervisorDatasetProp = {
  x: string;
  y: number;
  name: string;
  value: number;
  color: string;
  imgSrc: string | undefined;
  id: string;
};

const messageIds = [
  'insights.viewOptions.thisMonth',
  'insights.viewOptions.thisWeek',
  'insights.viewOptions.lastNinetyDays',
  'insights.viewOptions.custom'
];

const SuperVisorSourceByTimeChart: React.FC = () => {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  const messages = useMessages(messageIds);
  const [selectvalue, setSelectValue] = useState<string>(
    DropdownLabel.collectors
  );

  const onSelectValueChange = useCallback((event: SelectChangeEvent) => {
    setSelectValue(event.target.value);
  }, []);

  const isMetricsByCollectors = useMemo(() => {
    return selectvalue === DropdownLabel.collectors;
  }, [selectvalue]);

  const { selectedViewOptionSupervisor } = useSupervisorInsightContext();

  const labelValues = useGetSearchLabels();
  const { data, loading, isEmpty } = useGetSupervisorAnalyticsData(
    isMetricsByCollectors
      ? OrgAnalyticsDimensionType.MetricsByNoteSource
      : OrgAnalyticsDimensionType.MetricsByNoteCategory,
    labelValues,
    OrgAnalyticsContext.Supervisor
  );

  const { data: billableData } = useGetSupervisorAnalyticsData(
    isMetricsByCollectors
      ? OrgAnalyticsDimensionType.MetricsByNoteSourceBillable
      : OrgAnalyticsDimensionType.MetricsByNoteCategoryBillable,
    labelValues,
    OrgAnalyticsContext.Supervisor
  );

  const manualEntries = formatMessage({
    id: 'insights.timeBreakdownChart.manualEntries'
  });
  const {
    data: prevData,
    loading: prevLoading,
    prevStartDate
  } = useGetPreviousSupervisorData(
    isMetricsByCollectors
      ? OrgAnalyticsDimensionType.MetricsByNoteSource
      : OrgAnalyticsDimensionType.MetricsByNoteCategory,
    labelValues,
    OrgAnalyticsContext.Supervisor
  );

  const { data: prevBillableData } = useGetPreviousSupervisorData(
    isMetricsByCollectors
      ? OrgAnalyticsDimensionType.MetricsByNoteSourceBillable
      : OrgAnalyticsDimensionType.MetricsByNoteCategoryBillable,
    labelValues,
    OrgAnalyticsContext.Supervisor
  );

  const dataset = useMemo(() => {
    if (isEmpty) {
      return [];
    } else
      return transformAnalyticsDataSupervisor(
        data?.orgAnalytics?.analyticsResult.items,
        manualEntries
      );
  }, [isEmpty, data?.orgAnalytics?.analyticsResult.items, manualEntries]);

  const trendData = useMemo(() => {
    const isStartTimeValid = hasValidStartDate(prevStartDate || '');
    const currentTrendsData = trendsData(
      data?.orgAnalytics?.analyticsResult.items
    );
    const prevTrendsData = trendsData(
      prevData?.orgAnalytics?.analyticsResult.items
    );

    return isStartTimeValid && currentTrendsData && prevTrendsData
      ? {
          currentTrendsData: currentTrendsData,
          prevTrendsData: prevTrendsData
        }
      : undefined;
  }, [
    data?.orgAnalytics?.analyticsResult.items,
    prevData?.orgAnalytics?.analyticsResult.items,
    prevStartDate
  ]);

  const billableTrendsData = useMemo(() => {
    const isStartTimeValid = hasValidStartDate(prevStartDate || '');
    const billableTrendsData = trendsData(
      billableData?.orgAnalytics?.analyticsResult.items
    );
    const prevBillableTrendsData = trendsData(
      prevBillableData?.orgAnalytics?.analyticsResult.items
    );

    if (
      isStartTimeValid &&
      trendData &&
      billableTrendsData &&
      prevBillableTrendsData
    ) {
      return {
        currentTrendsData: Object.entries(trendData.currentTrendsData).reduce(
          (a, [key, value]) => {
            a[key] = getFormattedPercentage(
              billableTrendsData[key] ?? 0,
              value ?? 1
            );
            return a;
          },
          {} as Record<string, number>
        ),
        prevTrendsData: Object.entries(trendData.prevTrendsData).reduce(
          (a, [key, value]) => {
            a[key] = getFormattedPercentage(
              prevBillableTrendsData[key] ?? 0,
              value ?? 1
            );
            return a;
          },
          {} as Record<string, number>
        )
      };
    }
    return undefined;
  }, [
    billableData?.orgAnalytics?.analyticsResult.items,
    prevBillableData?.orgAnalytics?.analyticsResult.items,
    prevStartDate,
    trendData
  ]);

  const headerText = useMemo(() => {
    if (selectedViewOptionSupervisor === NoteInsightsViewOptions.THIS_WEEK) {
      return messages['insights.viewOptions.thisWeek'];
    } else if (
      selectedViewOptionSupervisor === NoteInsightsViewOptions.THIS_MONTH
    ) {
      return messages['insights.viewOptions.thisMonth'];
    } else if (
      selectedViewOptionSupervisor === NoteInsightsViewOptions.LAST_NINETY_DAYS
    )
      return messages['insights.viewOptions.lastNinetyDays'];
    return messages['insights.viewOptions.custom'];
  }, [messages, selectedViewOptionSupervisor]);
  const dataValues = useMemo(() => {
    return {
      dataset: dataset,
      loading: loading || prevLoading,
      isEmpty: isEmpty
    };
  }, [dataset, isEmpty, loading, prevLoading]);

  const isDisabled = useMemo(() => {
    return Boolean((isEmpty && isMetricsByCollectors) || loading);
  }, [isEmpty, isMetricsByCollectors, loading]);

  return (
    <Paper
      className={classes.container}
      elevation={3}
      data-testid={'source-by-time-chart-container'}
    >
      <SourceByTimeChart
        isDisabled={isDisabled}
        dataValues={dataValues}
        selectvalue={selectvalue}
        isMetricsByCollectors={true}
        onSelectValueChange={onSelectValueChange}
      />
      {!isEmpty && !loading && !prevLoading && (
        <Legend
          dataset={dataValues.dataset}
          isMetricsByCollectors={isMetricsByCollectors}
          columnHeader={headerText}
          trendData={trendData}
          billableTrendsData={billableTrendsData}
        />
      )}
    </Paper>
  );
};

export default React.memo(SuperVisorSourceByTimeChart);
